import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import { TODAY_CLOCK_IN } from "../../api/query";
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Popup from "../dashboard/Popup";
import { showError, showSuccess } from "../common/ToastNotification";
import { CREATE_BREAK } from "../../api/mutation";
import SkeletonLoader from "../common/SkeletonLoader";

const ActionLinks = () => {
    const navigate = useNavigate();
    const { userInfo, logout,isAdmin,developmentUser } = useAuth()
    const agencyId = userInfo?.agencies?.id;
    const employeeID = userInfo?.employee_id

    const [clockInPopup, setClockInPopup] = useState(false);
    async function handlePopupClose() {
        setClockInPopup(false)
        await getTodayClockIn({
            variables: {
                employee_id: parseInt(employeeID),
                agency_id: parseInt(agencyId)
            }
        })
    }

    //api
    const [
        getTodayClockIn,
        { data: todayClockInData, loading: todayClockInLoading, error },
    ] = useLazyQuery(TODAY_CLOCK_IN, {
        fetchPolicy: 'no-cache'
    });


    const [createBreak, { loading: createBreakLoading }] = useMutation(CREATE_BREAK, {
        fetchPolicy: 'no-cache',
    })

    if (error) {
        if ((error.message.includes("Session has expired. Please login again.")) || (error.message.includes("Unauthorized access, please login again."))) {
            showError(error.message)
            logout();
            navigate('/')
        }
    }

    useEffect(() => {
        if (employeeID && agencyId) {
            getTodayClockIn({
                variables: {
                    employee_id: parseInt(employeeID),
                    agency_id: parseInt(agencyId)
                }
            })
        }

    }, [employeeID, agencyId])

    const hasClockedIn = !!todayClockInData?.today_clock_in_out?.clock_in?.length;
    const clockInId = todayClockInData?.today_clock_in_out?.clock_in?.[0]?.id;

    const breaks = todayClockInData?.today_clock_in_out?.breaks || [];
    const latestBreak = breaks.length > 0 ? breaks[0] : null;
    const isOnBreak =
        latestBreak?.is_break_in === 'break_in' && !latestBreak?.end_time;
    const breakId = latestBreak?.id;


    const allowedEmployeeIDs = [ "1000099", "1000006", "1000014","1000108","1000029","1000012"];
    const allowedPeerEmployeeIDs = developmentUser;

    const data = [
        {
            module_name: "Actions",
            components: hasClockedIn ? [
                // {
                //     sub_module_name: "Add Task",
                //     path: "/add_task",
                // },
                 {
                    sub_module_name: "Add Goal",
                    path: "/create_goal_from_template",
                },
                {
                    sub_module_name: "Add Idea",
                    path: "/add_idea",
                },
                ...isAdmin
                    ? [
                             {
                    sub_module_name: "POD Management",
                    path: "/pod_management",
                },
                        {
                            sub_module_name: "Add Employee",
                            path: "/add_employee",
                        },
                    ]
                    : [],
                // {
                //     sub_module_name: "View Pre Defects",
                //     path: "/view_pre_defects",
                // },
                {
                    sub_module_name: "Employee-Task List",
                    path: "/employee_task_list",
                },
                {
                    sub_module_name: "Add To Do",
                    path: "/add_todo",
                },
                {
                    sub_module_name: "Add Meeting",
                    path: "/add_meeting",
                },
                {
                    sub_module_name: "Resource Overview",
                    path: "/resource_overiew",
                },
                {
                    sub_module_name: "View Ideas",
                    path: "/ideas",
                },
                {
                    sub_module_name: "Peer Points",
                    path: "/peer_points",
                },
                {
                    sub_module_name: "User Analytics",
                    path: "/user_profile",
                },
                {
                    sub_module_name: "Feature History",
                    path: "/feature_history",
                },
                  
                ...allowedPeerEmployeeIDs
                    ? [
                    
                        {
                            sub_module_name: "Peer Points Board",
                            path: "/peer_points_board",
                        },
                    ]
                    : [],
            ] : [     {
                    sub_module_name: "Resource Overview",
                    path: "/resource_overiew",
                },],
        },
        {
            module_name: "Attendance",
            components: [
                {
                    sub_module_name: "View Attendance",
                    path: "/view_attendance",
                },
                {
                    sub_module_name: "Apply Leave",
                    path: "/apply_leave",
                },
                {
                    sub_module_name: "Holidays List",
                    path: "/holidays",
                },
                  {
                            sub_module_name: "All Attendance",
                            path: "/all_attendance_new",
                        },
                ...(allowedEmployeeIDs.includes(employeeID)
                    ? [
                        {
                            sub_module_name: "Leave Management",
                            path: "/leave_management",
                        },
                        // {
                        //     sub_module_name: "All Attendance",
                        //     path: "/all_attendance",
                        // },
                        //                      
                         
                    ]
                    : []),
            ]
        },
        // {
        //     module_name: "Templates",
        //     components: [
        //         {
        //             sub_module_name: "Caryfy To Carebravo Transition",
        //             path: "/carebravo_transition_template",
        //         },
        //     ]
        // },
        {
            module_name: "Customer Success",
            components: [
                {
                    sub_module_name: "Support Dashboard",
                    path: "/support_dashboard",
                },
            ]
        },
    ];

    async function handleBreakInClick(clockInID: string) {
        const now = new Date();
        const pad = (n: any) => String(n).padStart(2, "0");
        const localTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.000Z`;

        const breakData = {
            clock_in_id: parseInt(clockInID),
            start_time: localTime,
            agencies_id: parseInt(agencyId),
            created_by: parseInt(employeeID),
            is_break_in: "break_in"
        }

        try {
            const response = await createBreak({ variables: { input: breakData } });
            if (response.data.create_breaks.status_code === 201) {
                showSuccess(response.data.create_breaks.message)

                await getTodayClockIn({
                    variables: {
                        employee_id: parseInt(employeeID),
                        agency_id: parseInt(agencyId)
                    }
                })
                // navigate('/dashboard')
            }
            if (response.data.create_breaks.status_code === 400) {
                showError(response.data.create_breaks.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    async function handleBreakOutClick(clockInID: string, id: string) {
        const now = new Date();
        const pad = (n: any) => String(n).padStart(2, "0");
        const localTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}.000Z`;

        const breakData = {
            id: parseInt(id),
            clock_in_id: parseInt(clockInID),
            end_time: localTime,
            agencies_id: parseInt(agencyId),
            updated_by: parseInt(employeeID),
            is_break_in: "break_out"
        }

        try {
            const response = await createBreak({ variables: { input: breakData } });
            if (response.data.create_breaks.status_code === 201) {
                showSuccess(response.data.create_breaks.message)

                await getTodayClockIn({
                    variables: {
                        employee_id: parseInt(employeeID),
                        agency_id: parseInt(agencyId)
                    }
                })
                // navigate('/dashboard')
            }
            if (response.data.create_breaks.status_code === 400) {
                showError(response.data.create_breaks.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    return (
        <>
            <div className="min-w-[30%] shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)]  action_askcary_section bg-white rounded-lg flex p-4 mq575:p-0 flex-col items-start justify-start gap-[20px]  max-w-[328px] text-sm mq950:min-w-[250px] mq950:max-w-[250px] mq575:min-w-full">
                <div className="min-w-full  rounded-lg bg-white box-border flex mq575:p-4 flex-col items-start justify-center gap-[22px] text-left text-sm text-dark-font font-body-reg  dark:bg-slate-800 mq575:min-w-full">
                    {data?.map((item: any) => {
                        return (
                            <>
                                <div className="grid flex-col items-start justify-start min-w-full ">
                                    <h5 className="self-stretch relative text-base m-0 my-0">
                                        <b>{item?.module_name}</b>
                                    </h5>
                                </div>
                                <div className="flex  justify-start min-w-full flex-wrap gap-[4px] mq575:gap-[8px]">
                                    {item?.components?.map((component: any) => {
                                        // Conditionally passing state only for '/clock_out'
                                        const shouldPassState = component?.path === "/clock_out";
                                        return (
                                            <div className="cursor-pointer	bg-main-blue rounded-radius-sm box-border dark:bg-slate-700 grid flex-col items-start justify-center p-1.5 gap-[8px] text-left text-sm text-dark-font font-body-reg border-[1px] border-solid border-surface-border dark:bg-slate-700">
                                                <div className="grid min-w-full gap-[4px]">
                                                    <Link
                                                        to={component?.path}
                                                        state={shouldPassState ? { todayClockInData: todayClockInData?.today_clock_in_out?.clock_in[0] } : undefined}
                                                    >
                                                        <p className="font-semibold dark:text-white m-0 text-white">
                                                            <b>{component?.sub_module_name}</b>
                                                        </p>
                                                    </Link>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Handle Clock In Button Manually if not clocked in */}
                                    {item.module_name === "Attendance" && (
                                        <>
                                            {!hasClockedIn ? (
                                                // Clock In
                                                <div
                                                    className="cursor-pointer bg-main-blue rounded-radius-sm box-border dark:bg-slate-700 grid flex-col items-start justify-center p-1.5 gap-[8px] text-left text-sm font-body-reg border-[1px] border-solid border-surface-border mq575:min-w-full"
                                                    onClick={() => setClockInPopup(true)}
                                                >
                                                    <div className="grid min-w-full gap-[4px]">
                                                        <p className="font-semibold dark:text-white m-0 text-white">
                                                            <b>Clock In</b>
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : isOnBreak ? (
                                                // Break Out
                                                <div
                                                    className="cursor-pointer bg-main-blue rounded-radius-sm box-border dark:bg-slate-700 grid flex-col items-start justify-center p-1.5 gap-[8px] text-left text-sm text-dark-font font-body-reg border-[1px] border-solid border-surface-border dark:bg-slate-700"
                                                    onClick={() => handleBreakOutClick(clockInId, breakId)}
                                                >
                                                    <div className="grid min-w-full gap-[4px]">
                                                        <p className="font-semibold dark:text-white m-0 text-white">
                                                            <b>Break Out</b>
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                // Break In + Clock Out
                                                <>
                                                    <div
                                                        className="cursor-pointer bg-main-blue rounded-radius-sm box-border dark:bg-slate-700 grid flex-col items-start justify-center p-1.5 gap-[8px] text-left text-sm text-dark-font font-body-reg border-[1px] border-solid border-surface-border dark:bg-slate-700"
                                                        onClick={() => handleBreakInClick(clockInId)}
                                                    >
                                                        <div className="grid min-w-full gap-[4px]">
                                                            <p className="font-semibold dark:text-white m-0 text-white">
                                                                <b>Break In</b>
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <Link
                                                        to="/clock_out"
                                                        state={{ todayClockInData: todayClockInData?.today_clock_in_out?.clock_in[0] }}
                                                    >
                                                        <div className="cursor-pointer	bg-main-blue rounded-radius-sm box-border dark:bg-slate-700 grid flex-col items-start justify-center p-1.5 gap-[8px] text-left text-sm text-dark-font font-body-reg border-[1px] border-solid border-surface-border dark:bg-slate-700">
                                                            <div className="grid min-w-full gap-[4px]">
                                                                <p className="font-semibold dark:text-white m-0 text-white">
                                                                    <b>Clock Out</b>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </Link>
                                                </>
                                            )}
                                        </>
                                    )
                                    }

                                    {/* {item.module_name === "Attendance" &&
                                    !hasClockedIn && (
                                        <div className="cursor-pointer	bg-main-blue rounded-radius-sm box-border dark:bg-slate-700 grid flex-col items-start justify-center p-1.5 gap-[8px] text-left text-sm text-dark-font font-body-reg border-[1px] border-solid border-surface-border dark:bg-slate-700"
                                            onClick={() => setClockInPopup(true)}
                                        >
                                            <div className="grid min-w-full gap-[4px]">
                                                <p className="font-semibold dark:text-white m-0 text-white">
                                                    <b>Clock In</b>
                                                </p>
                                            </div>
                                        </div>
                                    )} */}
                                </div>
                            </>
                        );
                    })}
                </div >

                {clockInPopup && (
                    <Popup
                        isOpen={clockInPopup}
                        onClose={handlePopupClose}
                        refetch={() => { }}
                    />
                )}
            </div >
            {(todayClockInLoading || createBreakLoading) && (
                <div className="min-w-[30%] shadow-[0px_1px_4px_rgba(0,_0,_0,_0.25)] action_askcary_section bg-surface-bg-primary rounded-lg flex p-4 mq575:p-0 flex-col items-start justify-start gap-[20px] max-w-[328px] text-sm mq950:min-w-[250px] mq950:max-w-[250px] mq575:min-w-full">
                    <div className="w-full flex flex-col gap-4">
                        <div className="flex justify-between items-center">
                            <SkeletonLoader count={1} height="h-5" width="w-24" />
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <SkeletonLoader count={1} height="h-8" width="w-20" borderRadius="rounded" />
                            <SkeletonLoader count={1} height="h-8" width="w-24" borderRadius="rounded" />
                            <SkeletonLoader count={1} height="h-8" width="w-28" borderRadius="rounded" />
                        </div>
                        <div className="flex justify-between items-center">
                            <SkeletonLoader count={1} height="h-5" width="w-28" />
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <SkeletonLoader count={1} height="h-8" width="w-24" borderRadius="rounded" />
                            <SkeletonLoader count={1} height="h-8" width="w-32" borderRadius="rounded" />
                            <SkeletonLoader count={1} height="h-8" width="w-20" borderRadius="rounded" />
                        </div>
                        <div className="flex justify-between items-center">
                            <SkeletonLoader count={1} height="h-5" width="w-24" />
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <SkeletonLoader count={1} height="h-8" width="w-28" borderRadius="rounded" />
                            <SkeletonLoader count={1} height="h-8" width="w-24" borderRadius="rounded" />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ActionLinks;
